import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NotificationStatusChip from '../messages/NotificationStatusChip';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/SaveAlt';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { trTR } from '@mui/x-date-pickers/locales';
import trLocale from 'date-fns/locale/tr';

import { fetchJson, getApiBase } from '../../apiBase';
import { formatSendStats } from './ExamCommunicationStatusPanel';
import { getStudentAvatar } from '../../shared/studentAvatar';

const api = () => {
  const base = `${getApiBase()}/admin`;
  return {
    list: (school) => `${base}/storybook-read-evaluations/${encodeURIComponent(school)}`,
    create: (school) => `${base}/storybook-read-evaluations/${encodeURIComponent(school)}`,
    detail: (school, evaluationId) =>
      `${base}/storybook-read-evaluations/${encodeURIComponent(school)}/${encodeURIComponent(evaluationId)}`,
    update: (school, evaluationId) =>
      `${base}/storybook-read-evaluations/${encodeURIComponent(school)}/${encodeURIComponent(evaluationId)}`,
    delete: (school, evaluationId) =>
      `${base}/storybook-read-evaluations/${encodeURIComponent(school)}/${encodeURIComponent(evaluationId)}`,
    sendPlan: (school, evaluationId) =>
      `${base}/storybook-read-evaluations/${encodeURIComponent(school)}/${encodeURIComponent(evaluationId)}/send-plan`,
    save: (school, evaluationId) =>
      `${base}/storybook-read-evaluations/${encodeURIComponent(school)}/${encodeURIComponent(evaluationId)}/results`,
    send: (school, evaluationId) =>
      `${base}/storybook-read-evaluations/${encodeURIComponent(school)}/${encodeURIComponent(evaluationId)}/send`,
    classes: (school) => `${base}/classes/${encodeURIComponent(school)}`,
    books: (school, params = '') => `${base}/books/${encodeURIComponent(school)}${params ? `?${params}` : ''}`,
  };
};

const humanDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('tr-TR');
  } catch (_) {
    return value;
  }
};

const RESULT_STATUS_ABSENT = 'absent';
const ABSENT_RESULT_TOKEN = '__absent__';

const hasScoreValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
const normalizeResultStatus = (value) => (String(value || '').trim().toLowerCase() === RESULT_STATUS_ABSENT ? RESULT_STATUS_ABSENT : null);
const isAbsentDraftValue = (value) => String(value ?? '').trim() === ABSENT_RESULT_TOKEN;
const hasResultEntry = (value, resultStatus = null) => (
  normalizeResultStatus(resultStatus) === RESULT_STATUS_ABSENT || isAbsentDraftValue(value) || hasScoreValue(value)
);

const assessmentResultSection = (value, resultStatus = null) => {
  if (normalizeResultStatus(resultStatus) === RESULT_STATUS_ABSENT || isAbsentDraftValue(value)) return 'absent';
  return hasScoreValue(value) ? 'completed' : 'pending';
};

const resultEntryValidationError = (value) => {
  const rawValue = String(value ?? '').trim();
  if (!rawValue || isAbsentDraftValue(rawValue)) return null;

  const numericValue = Number(rawValue);
  if (Number.isFinite(numericValue) && numericValue < 0) {
    return 'Negatif değer girilemez.';
  }
  if (!Number.isInteger(numericValue)) {
    return 'Tam sayı girin.';
  }

  if (numericValue > 100) return 'Puan 100’ü geçemez.';
  return null;
};

function StudentResultAvatar({ studentId, studentName }) {
  const theme = getStudentAvatar(studentId);
  return (
    <Avatar
      sx={{
        width: 48,
        height: 48,
        bgcolor: theme.bgColor,
        border: '2px solid',
        borderColor: theme.borderColor,
        fontSize: 25,
      }}
      aria-label={`${studentName || 'Öğrenci'} hayvan avatarı`}
    >
      {theme.avatar}
    </Avatar>
  );
}

const toPickerDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  const result = new Date(year, month - 1, day);
  return Number.isNaN(result.getTime()) ? null : result;
};

const toIsoDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const decorateEvaluation = (evaluation) => {
  if (!evaluation) return evaluation;
  const suffixParts = [];
  if (evaluation.exam_date) suffixParts.push(humanDate(evaluation.exam_date));
  if (evaluation.storybook_name) suffixParts.push(evaluation.storybook_name);
  const suffix = suffixParts.length ? ` • ${suffixParts.join(' · ')}` : '';
  return {
    ...evaluation,
    label: `${evaluation.title || 'İsimsiz'}${suffix}`,
  };
};

function AssessmentActionMenu({ evaluation, onEdit, onDelete, disabled }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const assessmentTitle = evaluation?.title || 'Değerlendirme';

  const closeMenu = () => setAnchorEl(null);
  const openMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const editAssessment = () => {
    closeMenu();
    onEdit(evaluation);
  };
  const deleteAssessment = () => {
    closeMenu();
    onDelete(evaluation);
  };

  return (
    <>
      <Tooltip title="Diğer işlemler">
        <span>
          <IconButton
            size="small"
            aria-label={`${assessmentTitle} için diğer işlemler`}
            onClick={openMenu}
            disabled={disabled}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        MenuListProps={{ 'aria-label': `${assessmentTitle} işlemleri` }}
      >
        <MenuItem onClick={editAssessment}>Güncelle</MenuItem>
        <MenuItem onClick={deleteAssessment}>Sil</MenuItem>
      </Menu>
    </>
  );
}

export default function StoryBookReadEvaluationManager({ schoolId, onError, onSuccess }) {
  const apis = useMemo(() => api(), []);
  const scoreInputRefs = useRef({});
  const emptyCreateForm = { title: '', exam_date: '', class_id: null, book_id: null, storybook_name: '', notes: '', reminder_enabled: true };
  const [evaluations, setEvaluations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [detail, setDetail] = useState({ rows: [], evaluation: null });
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [edits, setEdits] = useState({});
  const [resultErrors, setResultErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingPlan, setSendingPlan] = useState(false);
  const [sending, setSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [classes, setClasses] = useState([]);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createErrors, setCreateErrors] = useState({});
  const [createdEvaluation, setCreatedEvaluation] = useState(null);
  const [createBooks, setCreateBooks] = useState([]);
  const [loadingCreateBooks, setLoadingCreateBooks] = useState(false);
  const [showInlineBookCreate, setShowInlineBookCreate] = useState(false);
  const [inlineBookForm, setInlineBookForm] = useState({ title: '', page_count: '' });
  const [creatingInlineBook, setCreatingInlineBook] = useState(false);
  const [localAlert, setLocalAlert] = useState('');

  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editEvaluation, setEditEvaluation] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', exam_date: '', class_id: null, storybook_name: '', notes: '', reminder_enabled: true });
  const [editErrors, setEditErrors] = useState({});
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteEvaluation, setDeleteEvaluation] = useState(null);

  const selectedCreateClass = useMemo(() => {
    if (!createForm.class_id) return null;
    return classes.find((c) => String(c?.id) === String(createForm.class_id)) || null;
  }, [classes, createForm.class_id]);
  const selectedCreateClassGrade = useMemo(() => {
    const cls = selectedCreateClass;
    if (!cls) return '';
    const rawGrade = String(cls?.grade_level || '').trim();
    if (rawGrade) {
      const digits = /^\d+$/.test(rawGrade) ? rawGrade : (rawGrade.match(/\d+/)?.[0] || '');
      if (digits) return String(Number(digits));
    }
    const className = String(cls?.name || '').trim();
    const fromName = className.match(/^\s*(\d{1,2})/)?.[1] || '';
    return fromName ? String(Number(fromName)) : '';
  }, [selectedCreateClass]);

  const selectedEditClass = useMemo(() => {
    if (!editForm.class_id) return null;
    return classes.find((c) => String(c?.id) === String(editForm.class_id)) || null;
  }, [classes, editForm.class_id]);

  const rowMap = useMemo(() => {
    const map = new Map();
    (detail.rows || []).forEach((row) => map.set(row.student_id, row));
    return map;
  }, [detail.rows]);

  const draftRows = useMemo(() => {
    return (detail.rows || []).map((row) => {
      const override = Object.prototype.hasOwnProperty.call(edits, row.student_id)
        ? edits[row.student_id]
        : normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
          ? ABSENT_RESULT_TOKEN
          : row.result_value ?? '';
      return {
        ...row,
        draftValue: override ?? '',
      };
    });
  }, [detail.rows, edits]);

  const sortedDraftRows = useMemo(() => {
    return [...draftRows].sort((left, right) => {
      const sectionOrder = { pending: 0, absent: 1, completed: 2 };
      // Group saved results so students stay in place while scores are entered.
      const leftSection = assessmentResultSection(left.result_value, left.result_status);
      const rightSection = assessmentResultSection(right.result_value, right.result_status);
      if (leftSection !== rightSection) {
        return sectionOrder[leftSection] - sectionOrder[rightSection];
      }
      return String(left.student_name || '').localeCompare(String(right.student_name || ''), 'tr-TR');
    });
  }, [draftRows]);

  const hasChanges = useMemo(() => Object.keys(edits).length > 0, [edits]);

  const hasAnyResultValue = Boolean(editEvaluation?.completed_count) || (
    String(editEvaluation?.id) === String(detail.evaluation?.id) &&
    (detail.rows || []).some((row) => hasResultEntry(row.result_value, row.result_status))
  );

  const progressStats = useMemo(() => {
    const totalCount = draftRows.length;
    const enteredCount = draftRows.filter((row) => hasResultEntry(row.draftValue)).length;
    const missingCount = Math.max(totalCount - enteredCount, 0);
    const notifiedCount = (detail.rows || []).filter((row) => row?.notified).length;
    const queuedCount = (detail.rows || []).filter((row) => row?.notification_status === 'pending').length;
    const draftCount = Object.keys(edits).length;
    const completionPercent = totalCount ? Math.round((enteredCount / totalCount) * 100) : 0;

    return {
      totalCount,
      enteredCount,
      missingCount,
      notifiedCount,
      queuedCount,
      draftCount,
      completionPercent,
    };
  }, [detail.rows, draftRows, edits]);

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await fetchJson(apis.classes(schoolId));
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data)
          ? data
              .map((c) => {
                if (!c) return null;
                if (typeof c === 'string') return { id: c, name: c };
                return {
                  id: c?.id ?? c?.name,
                  name: c?.name ?? String(c?.id ?? ''),
                  grade_level: c?.grade_level ?? null,
                };
              })
              .filter(Boolean)
          : [];
        items.sort((a, b) => String(a?.name).localeCompare(String(b?.name), 'tr-TR'));
        setClasses(items);
      }
    } catch (err) {
      console.warn('classes load failed', err);
    }
  }, [apis, schoolId]);

  const loadCreateBooks = useCallback(async () => {
    if (!schoolId || !selectedCreateClassGrade) {
      setCreateBooks([]);
      return;
    }
    setLoadingCreateBooks(true);
    try {
      const q = new URLSearchParams({ type: 'okuma', grade: selectedCreateClassGrade });
      const res = await fetchJson(apis.books(schoolId, q.toString()));
      if (!res.ok) throw new Error('books_failed');
      const data = await res.json();
      const items = Array.isArray(data) ? data : [];
      items.sort((a, b) => String(a?.title || '').localeCompare(String(b?.title || ''), 'tr-TR'));
      setCreateBooks(items);
    } catch (err) {
      console.error(err);
      setCreateBooks([]);
      onError && onError('Kitap listesi yüklenemedi');
    } finally {
      setLoadingCreateBooks(false);
    }
  }, [apis, schoolId, selectedCreateClassGrade, onError]);

  useEffect(() => {
    loadCreateBooks();
  }, [loadCreateBooks]);

  useEffect(() => {
    setCreateForm((prev) => ({ ...prev, book_id: null, storybook_name: '' }));
    setShowInlineBookCreate(false);
    setInlineBookForm({ title: '', page_count: '' });
  }, [selectedCreateClassGrade]);

  const loadEvaluations = useCallback(
    async (focusEvaluationId = null) => {
      if (!schoolId) return;
      setLoadingList(true);
      try {
        const res = await fetchJson(apis.list(schoolId));
        if (!res.ok) throw new Error('list_failed');
        const data = await res.json();
        const items = (data || []).map(decorateEvaluation);
        setEvaluations(items);
        setSelectedEvaluation((prev) => {
          if (focusEvaluationId) {
            return items.find((it) => String(it.id) === String(focusEvaluationId)) || prev;
          }
          if (prev) {
            const refreshed = items.find((it) => String(it.id) === String(prev.id));
            return refreshed || prev;
          }
          return prev;
        });
      } catch (err) {
        console.error(err);
        onError && onError('Değerlendirme listesi yüklenemedi');
      } finally {
        setLoadingList(false);
      }
    },
    [apis, schoolId, onError]
  );

  const loadDetail = useCallback(
    async (evaluation) => {
      if (!evaluation || !schoolId) return;
      setLoadingDetail(true);
      try {
        const res = await fetchJson(apis.detail(schoolId, evaluation.id));
        if (!res.ok) throw new Error('detail_failed');
        const data = await res.json();
        setDetail({ evaluation: data.evaluation, rows: data.rows || [] });
        setEdits({});
        setResultErrors({});
        setLocalAlert('');
      } catch (err) {
        console.error(err);
        onError && onError('Değerlendirme detayı yüklenemedi');
        setDetail({ evaluation, rows: [] });
      } finally {
        setLoadingDetail(false);
      }
    },
    [apis, schoolId, onError]
  );

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    loadEvaluations();
  }, [loadEvaluations]);

  useEffect(() => {
    if (selectedEvaluation) {
      loadDetail(selectedEvaluation);
    }
  }, [selectedEvaluation, loadDetail]);

  const openAssessmentDetail = (evaluation) => {
    if (hasChanges && String(selectedEvaluation?.id) !== String(evaluation?.id)) {
      onError && onError('Başka bir değerlendirmeye geçmeden önce değişiklikleri kaydedin.');
      return;
    }
    if (String(selectedEvaluation?.id) !== String(evaluation?.id)) {
      setDetail({ evaluation, rows: [] });
      setEdits({});
      setResultErrors({});
      setLocalAlert('');
    }
    setSelectedEvaluation(evaluation);
  };

  const closeAssessmentDetail = () => {
    if (hasChanges) {
      onError && onError('Paneli kapatmadan önce değişiklikleri kaydedin.');
      return;
    }
    setSelectedEvaluation(null);
    setDetail({ evaluation: null, rows: [] });
    setEdits({});
    setResultErrors({});
    setLocalAlert('');
  };

  const handleResultChange = (studentId, value) => {
    const row = rowMap.get(studentId);
    const original = normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
      ? ABSENT_RESULT_TOKEN
      : String(row?.result_value ?? '').trim();
    const next = String(value ?? '');
    setResultErrors((prev) => ({ ...prev, [studentId]: resultEntryValidationError(next) }));
    setEdits((prev) => {
      const copy = { ...prev };
      if (next.trim() === original) {
        delete copy[studentId];
      } else {
        copy[studentId] = value;
      }
      return copy;
    });
  };

  const handleAbsentToggle = (studentId) => {
    setResultErrors((prev) => ({ ...prev, [studentId]: null }));
    const row = rowMap.get(studentId);
    const original = normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
      ? ABSENT_RESULT_TOKEN
      : String(row?.result_value ?? '').trim();

    setEdits((prev) => {
      const current = Object.prototype.hasOwnProperty.call(prev, studentId)
        ? String(prev[studentId] ?? '')
        : original;
      const next = current === ABSENT_RESULT_TOKEN ? '' : ABSENT_RESULT_TOKEN;
      const copy = { ...prev };
      if (next === original) {
        delete copy[studentId];
      } else {
        copy[studentId] = next;
      }
      return copy;
    });
  };

  const handleScoreKeyDown = (event, studentId) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const currentIndex = sortedDraftRows.findIndex((row) => String(row.student_id) === String(studentId));
    if (currentIndex < 0) return;

    const nextRow = sortedDraftRows[currentIndex + 1];
    if (!nextRow) return;

    const nextInput = scoreInputRefs.current[nextRow.student_id];
    if (nextInput && typeof nextInput.focus === 'function') {
      nextInput.focus();
      if (typeof nextInput.select === 'function') {
        nextInput.select();
      }
    }
  };

  const handleSave = async () => {
    if (!selectedEvaluation || !schoolId) return;
    const errors = {};
    draftRows.forEach((row) => {
      const error = resultEntryValidationError(row.draftValue);
      if (error) errors[row.student_id] = error;
    });
    setResultErrors(errors);
    if (Object.keys(errors).length) {
      setLocalAlert('Hatalı sonuçlar var. İşaretlenen alanları düzeltin.');
      return;
    }
    setSaving(true);
    try {
      const payloadRows = draftRows.map((row) => {
        const override = Object.prototype.hasOwnProperty.call(edits, row.student_id)
          ? edits[row.student_id]
          : normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
            ? ABSENT_RESULT_TOKEN
            : row.result_value ?? '';
        const resultValue = typeof override === 'string' ? override.trim() : override;
        const isAbsent = resultValue === ABSENT_RESULT_TOKEN;
        const normalized = resultValue === '' || isAbsent ? null : resultValue;
        return {
          student_id: row.student_id,
          result_value: normalized,
          result_status: isAbsent ? RESULT_STATUS_ABSENT : null,
        };
      });
      const res = await fetchJson(apis.save(schoolId, selectedEvaluation.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: payloadRows }),
      });
      if (!res.ok) throw new Error('save_failed');
      const data = await res.json();
      setDetail({ evaluation: data.evaluation, rows: data.rows || [] });
      setEdits({});
      setResultErrors({});
      setEvaluations((prev) => prev.map((item) => String(item.id) === String(data.evaluation?.id)
        ? decorateEvaluation(data.evaluation) : item));
      const completedBooksAddedCount = Number(data?.completed_books_added_count || 0);
      if (completedBooksAddedCount > 0) {
        const msg = `Sonuçlar kaydedildi • ${completedBooksAddedCount} öğrenci için (60 ve üzeri) kitap tamamlandı listesine eklendi`;
        setLocalAlert(msg);
        onSuccess && onSuccess(msg);
      } else {
        setLocalAlert('Sonuçlar kaydedildi');
        onSuccess && onSuccess('Sonuçlar kaydedildi');
      }
    } catch (err) {
      console.error(err);
      onError && onError('Sonuçlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!selectedEvaluation || !schoolId) return;
    setSending(true);
    try {
      const res = await fetchJson(apis.send(schoolId, selectedEvaluation.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('send_failed');
      const data = await res.json();
      setLocalAlert(`Kuyruğa eklendi • ${data.queued || 0} mesaj`);
      onSuccess && onSuccess('Sonuç bildirimleri kuyruğa eklendi');
      await loadDetail(selectedEvaluation);
    } catch (err) {
      console.error(err);
      onError && onError('Mesajlar gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const sendPlanForEvaluation = async (evaluationId) => {
    const res = await fetchJson(apis.sendPlan(schoolId, evaluationId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error('send_plan_failed');
    return res.json();
  };

  const resetCreateState = () => {
    setCreateForm(emptyCreateForm);
    setCreateErrors({});
    setCreatedEvaluation(null);
    setShowInlineBookCreate(false);
    setInlineBookForm({ title: '', page_count: '' });
  };

  const hideCreate = () => {
    setShowCreate(false);
    resetCreateState();
  };

  const toggleCreate = () => {
    if (showCreate) {
      closeCreate();
      return;
    }
    resetCreateState();
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (creating || creatingInlineBook || sendingPlan) return;
    hideCreate();
  };
  const handleCreateInlineBook = async () => {
    if (!schoolId) return;
    const title = String(inlineBookForm.title || '').trim();
    if (!title) {
      onError && onError('Kitap adı gerekli');
      return;
    }
    if (!selectedCreateClassGrade) {
      onError && onError('Önce şube seçin');
      return;
    }
    setCreatingInlineBook(true);
    try {
      const rawPage = String(inlineBookForm.page_count || '').trim();
      const page_count = rawPage === '' ? null : Math.max(0, parseInt(rawPage, 10) || 0);
      const payload = {
        type: 'okuma',
        grade: selectedCreateClassGrade,
        title,
        page_count,
      };
      const res = await fetchJson(apis.books(schoolId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('book_create_failed');
      const created = await res.json();
      setCreateBooks((prev) => {
        const merged = [...prev.filter((b) => String(b?.id) !== String(created?.id)), created];
        merged.sort((a, b) => String(a?.title || '').localeCompare(String(b?.title || ''), 'tr-TR'));
        return merged;
      });
      setCreateForm((prev) => ({
        ...prev,
        book_id: created?.id ?? null,
        storybook_name: String(created?.title || title),
      }));
      setCreateErrors((prev) => ({ ...prev, book_id: null }));
      setShowInlineBookCreate(false);
      setInlineBookForm({ title: '', page_count: '' });
      onSuccess && onSuccess('Kitap oluşturuldu ve seçildi');
    } catch (err) {
      console.error(err);
      onError && onError('Kitap oluşturulamadı');
    } finally {
      setCreatingInlineBook(false);
    }
  };

  const handleCreateEvaluation = async () => {
    const nextErrors = {};
    if (!createForm.title?.trim()) {
      nextErrors.title = 'Başlık gerekli';
    }
    if (!String(createForm.exam_date || '').trim()) {
      nextErrors.exam_date = 'Tarih gerekli';
    }
    if (!createForm.class_id) {
      nextErrors.class_id = 'Şube gerekli';
    }
    if (!createForm.book_id) {
      nextErrors.book_id = 'Kitap seçin veya yeni kitap oluşturun';
    }
    setCreateErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      onError && onError(Object.values(nextErrors)[0]);
      return;
    }
    setCreating(true);
    try {
      const payload = {
        title: createForm.title,
        exam_date: createForm.exam_date || null,
        class_id: createForm.class_id,
        book_id: createForm.book_id,
        storybook_name: createForm.storybook_name || null,
        notes: createForm.notes || null,
        reminder_enabled: !!createForm.reminder_enabled,
      };
      const res = await fetchJson(apis.create(schoolId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('create_failed');
      const evaluation = await res.json();
      await loadEvaluations(evaluation.id);
      setCreatedEvaluation({
        id: evaluation.id,
        title: evaluation.title || createForm.title,
        exam_date: evaluation.exam_date || createForm.exam_date || '',
        class_label: selectedCreateClass?.name || '-',
        storybook_name: createForm.storybook_name || evaluation.storybook_name || '',
        reminder_enabled: !!createForm.reminder_enabled,
      });
      setLocalAlert('Yeni değerlendirme oluşturuldu');
      onSuccess && onSuccess('Yeni değerlendirme oluşturuldu');
    } catch (err) {
      console.error(err);
      onError && onError('Değerlendirme oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const handleSendCreatedPlan = async () => {
    if (!createdEvaluation || !schoolId) return;
    setSendingPlan(true);
    try {
      const stats = await sendPlanForEvaluation(createdEvaluation.id);
      setLocalAlert(`Değerlendirme oluşturuldu ve plan kuyruğa eklendi • ${formatSendStats(stats)}`);
      onSuccess && onSuccess('Yeni değerlendirme oluşturuldu ve plan bildirimleri kuyruğa eklendi');
      await loadEvaluations(createdEvaluation.id);
      hideCreate();
    } catch (err) {
      console.error(err);
      onError && onError('Plan mesajları gönderilemedi');
    } finally {
      setSendingPlan(false);
    }
  };

  const disableSend =
    !detail.rows?.some((row) => hasResultEntry(row.result_value, row.result_status)) ||
    hasChanges ||
    sending ||
    sendingPlan;

  const disableListActions = hasChanges || saving || sending || sendingPlan || loadingDetail;

  const openEdit = (evaluation) => {
    if (!evaluation) return;
    setEditEvaluation(evaluation);
    setEditErrors({});
    setEditForm({
      title: evaluation.title || '',
      exam_date: evaluation.exam_date || '',
      class_id: evaluation.class_id ?? null,
      storybook_name: evaluation.storybook_name || '',
      notes: evaluation.notes || '',
      reminder_enabled: evaluation.reminder_enabled !== false,
    });
    setShowEdit(true);
  };

  const closeEdit = () => {
    if (editing) return;
    setShowEdit(false);
    setEditEvaluation(null);
    setEditErrors({});
  };

  const handleUpdateEvaluation = async () => {
    if (!editEvaluation || !schoolId) return;
    const nextErrors = {};
    if (!editForm.title?.trim()) {
      nextErrors.title = 'Başlık gerekli';
    }
    if (!String(editForm.exam_date || '').trim()) {
      nextErrors.exam_date = 'Tarih gerekli';
    }
    if (!editForm.class_id) {
      nextErrors.class_id = 'Şube gerekli';
    }
    setEditErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      onError && onError(Object.values(nextErrors)[0]);
      return;
    }

    setEditing(true);
    try {
      const payload = {
        title: editForm.title,
        exam_date: editForm.exam_date || null,
        class_id: editForm.class_id,
        storybook_name: editForm.storybook_name || null,
        notes: editForm.notes || null,
        reminder_enabled: !!editForm.reminder_enabled,
      };
      const res = await fetchJson(apis.update(schoolId, editEvaluation.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('update_failed');
      setShowEdit(false);
      setEditEvaluation(null);
      await loadEvaluations();
      if (String(selectedEvaluation?.id) === String(editEvaluation.id)) {
        await loadDetail({ id: editEvaluation.id });
      }
      onSuccess && onSuccess('Değerlendirme güncellendi');
    } catch (err) {
      console.error(err);
      onError && onError('Değerlendirme güncellenemedi');
    } finally {
      setEditing(false);
    }
  };

  const openDelete = (evaluation = selectedEvaluation) => {
    if (!evaluation) return;
    setDeleteEvaluation(evaluation);
    setShowDelete(true);
  };

  const closeDelete = () => {
    if (deleting) return;
    setShowDelete(false);
    setDeleteEvaluation(null);
  };

  const handleDeleteEvaluation = async () => {
    if (!deleteEvaluation || !schoolId) return;
    const deletedEvaluation = deleteEvaluation;
    setDeleting(true);
    try {
      const res = await fetchJson(apis.delete(schoolId, deletedEvaluation.id), { method: 'DELETE' });
      if (!res.ok) throw new Error('delete_failed');
      setShowDelete(false);
      setDeleteEvaluation(null);
      if (String(selectedEvaluation?.id) === String(deletedEvaluation.id)) {
        setSelectedEvaluation(null);
        setDetail({ rows: [], evaluation: null });
        setEdits({});
      }
      await loadEvaluations();
      onSuccess && onSuccess('Değerlendirme silindi');
    } catch (err) {
      console.error(err);
      onError && onError('Değerlendirme silinemedi');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={trLocale}
      localeText={trTR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
    <Box>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack spacing={1.5}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs="auto" md="auto">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Yeni Değerlendirme">
                        <span>
                          <IconButton
                            onClick={toggleCreate}
                            size="small"
                            color="primary"
                            sx={{
                              display: { xs: 'inline-flex', md: 'none' },
                              border: '1px solid',
                              borderColor: 'primary.main',
                            }}
                          >
                            <AddCircleOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={toggleCreate}
                        size="small"
                        sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                      >
                        Yeni Değerlendirme
                      </Button>
                    </Stack>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2" color="text.secondary">
                      Sonuç girmek için aşağıdaki hikaye değerlendirmelerinden birini seçin.
                    </Typography>
                  </Grid>
                  <Grid item xs="auto" md="auto">
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Tooltip title="Yenile">
                        <span>
                          <IconButton
                            onClick={() => {
                              loadEvaluations();
                              if (selectedEvaluation) loadDetail(selectedEvaluation);
                            }}
                            disabled={loadingList || loadingDetail}
                            size="small"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {loadingList && !evaluations.length ? (
                <Paper variant="outlined" sx={{ py: 5 }}>
                  <Stack
                    role="status"
                    aria-live="polite"
                    alignItems="center"
                    spacing={1.5}
                    color="text.secondary"
                  >
                    <CircularProgress size={28} aria-label="Hikaye değerlendirmeleri yükleniyor" />
                    <Typography variant="body2">Hikaye değerlendirmeleri yükleniyor…</Typography>
                  </Stack>
                </Paper>
              ) : !evaluations.length ? (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Henüz değerlendirme oluşturulmadı. Yukarıdaki “Yeni Değerlendirme” butonuyla başlayın.</Typography>
                </Box>
              ) : (
                <>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ px: 2, py: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800}>Hikaye Değerlendirmeleri</Typography>
                      <Typography variant="caption" color="text.secondary">Bir değerlendirmeye tıklayarak öğrenci sonuç girişini açın.</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">{evaluations.length} değerlendirme</Typography>
                  </Stack>
                  <TableContainer>
                    <Table size="small" aria-label="Hikaye değerlendirmeleri">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell>#</TableCell>
                          <TableCell>Değerlendirme</TableCell>
                          <TableCell>Tarih</TableCell>
                          <TableCell>Kapsam</TableCell>
                          <TableCell>Tür</TableCell>
                          <TableCell>Sonuç girişi</TableCell>
                          <TableCell aria-label="İşlemler" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {evaluations.map((evaluation, index) => {
                          const isSelected = String(selectedEvaluation?.id) === String(evaluation.id);
                          const scopeLabel = evaluation.class_name || '—';
                          const isSelectedDetail = isSelected && detail.evaluation && String(detail.evaluation.id) === String(evaluation.id);
                          const enteredCount = isSelectedDetail
                            ? progressStats.enteredCount
                            : Number(evaluation.completed_count || 0);
                          const totalCount = isSelectedDetail
                            ? progressStats.totalCount
                            : Number(evaluation.student_count || 0);
                          const completion = totalCount ? Math.round((enteredCount / totalCount) * 100) : 0;
                          return (
                            <TableRow
                              hover
                              key={evaluation.id}
                              tabIndex={0}
                              selected={isSelected}
                              onClick={() => openAssessmentDetail(evaluation)}
                              onKeyDown={(event) => event.target === event.currentTarget && event.key === 'Enter' && openAssessmentDetail(evaluation)}
                              sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 1.15 } }}
                            >
                              <TableCell>{evaluations.length - index}</TableCell>
                              <TableCell><Typography variant="body2" fontWeight={700}>{evaluation.title}</Typography>{evaluation.storybook_name && <Typography variant="caption" color="text.secondary">{evaluation.storybook_name}</Typography>}</TableCell>
                              <TableCell>{evaluation.exam_date ? humanDate(evaluation.exam_date) : '—'}</TableCell>
                              <TableCell>{scopeLabel}</TableCell>
                              <TableCell>100 üzerinden puan</TableCell>
                              <TableCell sx={{ minWidth: 150 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <LinearProgress
                                    variant="determinate"
                                    value={completion}
                                    sx={{ flex: 1, minWidth: 60, height: 8, borderRadius: 99 }}
                                  />
                                  <Typography variant="caption" noWrap>{enteredCount}/{totalCount}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <AssessmentActionMenu
                                  evaluation={evaluation}
                                  onEdit={openEdit}
                                  onDelete={openDelete}
                                  disabled={disableListActions}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Drawer
                  anchor="right"
                  open={Boolean(selectedEvaluation)}
                  onClose={closeAssessmentDetail}
                  PaperProps={{
                    sx: {
                      width: { xs: '100%', sm: 720, md: 840 },
                      maxWidth: '100vw',
                      bgcolor: '#f7faff',
                    },
                  }}
                >
                  <Box sx={{ minHeight: '100%' }}>
                    <Box
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        px: { xs: 2, sm: 2.5 },
                        py: 2,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="overline" fontWeight={800}>Hikaye değerlendirmesi</Typography>
                          <Typography variant="h6" fontWeight={800} noWrap>{detail.evaluation?.title || selectedEvaluation?.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {[detail.evaluation?.exam_date ? humanDate(detail.evaluation.exam_date) : null,
                              detail.evaluation?.class_name,
                              detail.evaluation?.storybook_name, '100 üzerinden puan'].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <IconButton aria-label="Değerlendirmeyi kapat" onClick={closeAssessmentDetail}>
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                    </Box>
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Paper variant="outlined" sx={{ p: 1.25 }}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={`${progressStats.enteredCount}/${progressStats.totalCount} tamamlandı`}
                          />
                          <Chip
                            size="small"
                            color={progressStats.missingCount > 0 ? 'warning' : 'default'}
                            variant="outlined"
                            label={`${progressStats.missingCount} bekleyen`}
                          />
                          <NotificationStatusChip status={detail.evaluation?.result_message_status} />
                          {hasChanges && (
                            <Chip size="small" label={`${progressStats.draftCount} kaydedilmemiş`} color="warning" />
                          )}
                        </Stack>
                      </Paper>
                      {localAlert && (
                        <Alert severity={localAlert.startsWith('Hatalı') ? 'error' : 'success'} variant="outlined">
                          {localAlert}
                        </Alert>
                      )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {!selectedEvaluation ? (
                      <Alert severity="info">Sonuç girmek için hikaye değerlendirmelerinden birine tıklayın.</Alert>
                    ) : loadingDetail ? (
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : !detail.rows?.length ? (
                      <Alert severity="info">Seçilen değerlendirme için öğrenci bulunamadı.</Alert>
                    ) : (
                      <Stack spacing={1.5}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={700}>Öğrenci Sonuçları</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Her öğrenci için satırdaki 100 üzerinden puanı girin. Eksik sonuçlar üstte gösterilir.
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                İpucu: Sonucu yazıp Enter'a bastığınızda bir sonraki öğrenciye geçebilirsiniz.
                              </Typography>
                            </Box>
                            <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
                              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  startIcon={<SaveIcon />}
                                  disabled={!hasChanges || saving}
                                  onClick={handleSave}
                                  size="small"
                                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                                >
                                  Sonuçları Kaydet
                                </Button>
                                <Button
                                  variant="contained"
                                  color="success"
                                  startIcon={<WhatsAppIcon />}
                                  disabled={disableSend}
                                  onClick={handleSend}
                                  size="small"
                                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                                >
                                  Sonuçları Gönder
                                </Button>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Paper>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 1fr)',
                            gap: 0.75,
                          }}
                        >
                          {sortedDraftRows.map((row, index) => {
                            const value = Object.prototype.hasOwnProperty.call(edits, row.student_id)
                              ? edits[row.student_id]
                              : row.draftValue;
                            const isAbsent = isAbsentDraftValue(value);
                            const hasResult = hasResultEntry(value);
                            const section = assessmentResultSection(
                              row.result_value,
                              row.result_status,
                            );
                            const previous = sortedDraftRows[index - 1];
                            const previousSection = assessmentResultSection(
                              previous?.result_value,
                              previous?.result_status,
                            );
                            const showSectionHeading = index === 0 || section !== previousSection;
                            const sectionLabel = section === 'pending'
                              ? 'Puan bekleyenler'
                              : section === 'absent'
                                ? 'Girmedi olarak işaretlenenler'
                                : 'Değerlendirilenler';
                            const avatarTheme = getStudentAvatar(row.student_id);
                            const validationError = resultErrors[row.student_id];
                            return (
                              <React.Fragment key={row.student_id}>
                                {showSectionHeading && (
                                  <Typography
                                    variant="overline"
                                    sx={{ mt: index === 0 ? 0 : 1.25, px: 0.5, fontWeight: 800, letterSpacing: 0.55, color: 'text.secondary' }}
                                  >
                                    {sectionLabel}
                                  </Typography>
                                )}
                              <Paper
                                role="group"
                                aria-label={`${row.student_name || 'Öğrenci'} sonucu`}
                                variant="outlined"
                                sx={{
                                  px: { xs: 1.25, sm: 1.5 },
                                  py: 1,
                                  borderRadius: 2.5,
                                  borderColor: hasResult ? avatarTheme.borderColor : 'divider',
                                  bgcolor: hasResult ? avatarTheme.bgColor : 'background.paper',
                                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                                  '&:hover': { borderColor: avatarTheme.borderColor },
                                  '&:focus-within': { boxShadow: 2 },
                                }}
                              >
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 1.5 }} alignItems={{ md: 'center' }}>
                                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: { md: 300 }, flex: 1 }}>
                                    <StudentResultAvatar studentId={row.student_id} studentName={row.student_name} />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography variant="subtitle2" fontWeight={700} noWrap>{row.student_name}</Typography>
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        #{row.student_no ?? row.student_id} · {row.class_name || 'Şube bilgisi yok'}
                                      </Typography>
                                    </Box>
                                    <Chip label={section === 'pending' ? 'Bekliyor' : section === 'absent' ? 'Girmedi' : 'Tamamlandı'} color={section === 'completed' ? 'success' : section === 'absent' ? 'warning' : 'default'} variant={section === 'pending' ? 'outlined' : 'filled'} size="small" sx={{ fontWeight: 600 }} />
                                  </Stack>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', md: 250 }, flexShrink: 0 }}>
                                    <TextField
                                      label="100 üzerinden puan"
                                      type="text"
                                      inputMode="numeric"
                                      inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                        min: 0,
                                        max: 100,
                                      }}
                                      size="small"
                                      fullWidth
                                      value={isAbsent ? '' : value}
                                      placeholder={isAbsent ? 'Girmedi' : 'Örn. 85'}
                                      disabled={isAbsent}
                                      error={Boolean(validationError)}
                                      helperText={validationError || ' '}
                                      onChange={(event) => {
                                        const nextValue = event.target.value.replace(/[^0-9]/g, '');
                                        handleResultChange(row.student_id, nextValue);
                                      }}
                                      onKeyDown={(event) => handleScoreKeyDown(event, row.student_id)}
                                      inputRef={(element) => {
                                        if (element) {
                                          scoreInputRefs.current[row.student_id] = element;
                                        } else {
                                          delete scoreInputRefs.current[row.student_id];
                                        }
                                      }}
                                      sx={{ '& input': { textAlign: 'center', fontWeight: 700, fontSize: '1.1rem' } }}
                                    />
                                    <Chip
                                      label="Girmedi"
                                      size="small"
                                      color={isAbsent ? 'warning' : 'default'}
                                      variant={isAbsent ? 'filled' : 'outlined'}
                                      onClick={() => handleAbsentToggle(row.student_id)}
                                    />
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary" sx={{ width: { md: 155 }, flexShrink: 0 }}>
                                    {isAbsent ? 'Öğrenci değerlendirmeye katılmadı.' : hasResult ? 'Puan kayda hazır.' : '0–100 arası puan girin.'}
                                  </Typography>
                                </Stack>
                              </Paper>
                              </React.Fragment>
                            );
                          })}
                        </Box>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1, width: '100%' }}>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<SaveIcon />}
                            disabled={!hasChanges || saving}
                            onClick={handleSave}
                            size="small"
                            sx={{ flex: { xs: 1, sm: 'initial' } }}
                          >
                            Sonuçları Kaydet
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<WhatsAppIcon />}
                            disabled={disableSend}
                            onClick={handleSend}
                            size="small"
                            sx={{ flex: { xs: 1, sm: 'initial' } }}
                          >
                            Sonuçları Gönder
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Grid>
                </Grid>
                    </Box>
                  </Box>
                </Drawer>
                </>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={showCreate}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') closeCreate();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{createdEvaluation ? '2. Adım: Planı Gönder' : 'Yeni Değerlendirme'}</DialogTitle>
        <DialogContent>
          {createdEvaluation ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="success" variant="outlined">
                Değerlendirme oluşturuldu. Şimdi plan mesajını velilere gönderme adımındasınız.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" fontWeight={600}>{createdEvaluation.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tarih: {createdEvaluation.exam_date ? humanDate(createdEvaluation.exam_date) : '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Şube: {createdEvaluation.class_label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Kitap: {createdEvaluation.storybook_name || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hatırlatma: {createdEvaluation.reminder_enabled ? 'Açık' : 'Kapalı'}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Değerlendirme başlığı"
                required
                fullWidth
                value={createForm.title}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setCreateForm((prev) => ({ ...prev, title: nextValue }));
                  setCreateErrors((prev) => ({ ...prev, title: nextValue.trim() ? null : prev.title }));
                }}
                error={Boolean(createErrors.title)}
                helperText={createErrors.title || ''}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                <DatePicker
                  label="Tarih"
                  value={toPickerDate(createForm.exam_date)}
                  onChange={(value) => {
                    const nextValue = toIsoDate(value);
                    setCreateForm((prev) => ({ ...prev, exam_date: nextValue }));
                    setCreateErrors((prev) => ({ ...prev, exam_date: nextValue ? null : prev.exam_date }));
                  }}
                  format="dd.MM.yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(createErrors.exam_date),
                      helperText: createErrors.exam_date || '',
                      sx: { minWidth: 160 },
                    },
                  }}
                />
                <Autocomplete
                  fullWidth
                  options={classes}
                  value={selectedCreateClass}
                  onChange={(_, value) => {
                    setCreateForm((prev) => ({ ...prev, class_id: value?.id ?? null }));
                    setCreateErrors((prev) => ({ ...prev, class_id: value?.id ? null : prev.class_id }));
                  }}
                  getOptionLabel={(option) => option?.name || ''}
                  renderInput={(params) => <TextField {...params} label="Şube" error={Boolean(createErrors.class_id)} helperText={createErrors.class_id || ''} />}
                />
              </Stack>
              {!selectedCreateClassGrade && (
                <Alert severity="info" variant="outlined">
                  Önce şube seçin; kitap listesi sınıf düzeyine göre yüklenecek.
                </Alert>
              )}
              {selectedCreateClassGrade && (
                <>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Sınıf düzeyine uygun kitaplar"
                    value={createForm.book_id || ''}
                    onChange={(e) => {
                      const nextId = e.target.value || '';
                      const selected = createBooks.find((b) => String(b?.id) === String(nextId));
                      setCreateForm((prev) => ({
                        ...prev,
                        book_id: nextId || null,
                        storybook_name: selected?.title || '',
                      }));
                      setCreateErrors((prev) => ({ ...prev, book_id: nextId ? null : prev.book_id }));
                    }}
                    disabled={loadingCreateBooks}
                    error={Boolean(createErrors.book_id)}
                    helperText={createErrors.book_id || ''}
                  >
                    <MenuItem value="">Kitap seçin</MenuItem>
                    {createBooks.map((book) => (
                      <MenuItem key={book.id} value={book.id}>
                        {book.title}{book.page_count != null ? ` (${book.page_count} sf)` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Button size="small" onClick={() => setShowInlineBookCreate((prev) => !prev)}>
                      {showInlineBookCreate ? 'Yeni kitap alanını gizle' : 'Listede yok mu? Yeni kitap ekle'}
                    </Button>
                    {loadingCreateBooks && <CircularProgress size={18} />}
                  </Stack>
                  {showInlineBookCreate && (
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1.5}>
                        <TextField
                          size="small"
                          label="Yeni kitap adı"
                          value={inlineBookForm.title}
                          onChange={(e) => setInlineBookForm((prev) => ({ ...prev, title: e.target.value }))}
                        />
                        <TextField
                          size="small"
                          label="Sayfa sayısı"
                          type="number"
                          inputProps={{ min: 0 }}
                          value={inlineBookForm.page_count}
                          onChange={(e) => setInlineBookForm((prev) => ({ ...prev, page_count: e.target.value }))}
                        />
                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            size="small"
                            disabled={creatingInlineBook}
                            onClick={handleCreateInlineBook}
                          >
                            Kitabı Oluştur ve Seç
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                </>
              )}
              <TextField
                label="Not"
                fullWidth
                multiline
                minRows={3}
                value={createForm.notes}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
              <FormControlLabel
                control={<Switch checked={!!createForm.reminder_enabled} onChange={(e) => setCreateForm((prev) => ({ ...prev, reminder_enabled: e.target.checked }))} />}
                label="1 gün önce hatırlatma mesajı gönder"
              />
              <Alert severity="info" variant="outlined">
                İlk adımda değerlendirme oluşturulur. Oluşturma sonrası otomatik olarak plan gönderme adımına geçersiniz.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        {createdEvaluation ? (
          <DialogActions>
            <Button onClick={closeCreate} disabled={sendingPlan}>Atla</Button>
            <Button variant="contained" color="success" onClick={handleSendCreatedPlan} disabled={sendingPlan}>
              Planı Gönder
            </Button>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button onClick={closeCreate} disabled={creating || creatingInlineBook}>Vazgeç</Button>
            <Button variant="contained" onClick={handleCreateEvaluation} disabled={creating || creatingInlineBook}>
              Oluştur
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog open={showEdit} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Değerlendirmeyi Düzenle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Değerlendirme başlığı"
              required
              fullWidth
              value={editForm.title}
              onChange={(e) => {
                const nextValue = e.target.value;
                setEditForm((prev) => ({ ...prev, title: nextValue }));
                setEditErrors((prev) => ({ ...prev, title: nextValue.trim() ? null : prev.title }));
              }}
              error={Boolean(editErrors.title)}
              helperText={editErrors.title || ''}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
              <DatePicker
                label="Tarih"
                value={toPickerDate(editForm.exam_date || '')}
                onChange={(value) => {
                  const nextValue = toIsoDate(value);
                  setEditForm((prev) => ({ ...prev, exam_date: nextValue }));
                  setEditErrors((prev) => ({ ...prev, exam_date: nextValue ? null : prev.exam_date }));
                }}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(editErrors.exam_date),
                    helperText: editErrors.exam_date || '',
                    sx: { minWidth: 160 },
                  },
                }}
              />
              <Autocomplete
                fullWidth
                options={classes}
                value={selectedEditClass}
                onChange={(_, value) => {
                  setEditForm((prev) => ({ ...prev, class_id: value?.id ?? null }));
                  setEditErrors((prev) => ({ ...prev, class_id: value?.id ? null : prev.class_id }));
                }}
                isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                getOptionLabel={(option) => option?.name || ''}
                renderInput={(params) => <TextField {...params} label="Şube" error={Boolean(editErrors.class_id)} helperText={editErrors.class_id || ''} />}
                disabled={hasAnyResultValue}
              />
            </Stack>
            {hasAnyResultValue && (
              <Alert severity="info" variant="outlined">
                Sonuç girilmişse şube değiştirilemez.
              </Alert>
            )}
            <TextField
              label="Kitap / Hikaye"
              fullWidth
              value={editForm.storybook_name}
              onChange={(e) => setEditForm((prev) => ({ ...prev, storybook_name: e.target.value }))}
            />
            <TextField
              label="Not"
              fullWidth
              multiline
              minRows={3}
              value={editForm.notes}
              onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
            />
            <FormControlLabel
              control={<Switch checked={!!editForm.reminder_enabled} onChange={(e) => setEditForm((prev) => ({ ...prev, reminder_enabled: e.target.checked }))} />}
              label="1 gün önce hatırlatma mesajı gönder"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editing}>Vazgeç</Button>
          <Button variant="contained" onClick={handleUpdateEvaluation} disabled={editing}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDelete} onClose={closeDelete} fullWidth maxWidth="xs">
        <DialogTitle>Değerlendirmeyi Sil</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Seçili değerlendirme ve tüm sonuçları silinecek. Bu işlem geri alınamaz.
          </Typography>
          {deleteEvaluation && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {deleteEvaluation.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deleteEvaluation.exam_date ? humanDate(deleteEvaluation.exam_date) : '-'}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>Vazgeç</Button>
          <Button color="error" variant="contained" onClick={handleDeleteEvaluation} disabled={deleting}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </LocalizationProvider>
  );
}
